import { put, call, select, takeLatest } from 'redux-saga/effects';

import { ADVOCATE_ROUTES, EMPLOYER_ROUTES } from 'consts';
import { i18n } from 'locales/i18n';
import {
  login,
  getAllPlatform,
  accessRouter,
  sessionRegisterToken,
} from 'services/auth';
import { AsyncAction } from 'types/Action';
import {
  LoginMeta,
  LoginPayload,
  PlatForm,
  SessionOpenItem,
} from 'types/store/LoginState';
import { TrackAutoType } from 'types/store/TrackLoginMeta';
import { Store } from 'types/store/Store';
import accessRouterActions, {
  AccessRouterMeta,
} from 'store/actions/auth/accessRouter';
import { GenericResponse } from 'types/GenericResponse';
import navigationService from 'utils/navigationService';
import getSessionToken from 'store/actions/login/sessionToken';

import {
  loginActions,
  authActions,
  registerEbWithExistedAccountActions,
  getOnboardActions,
  openToast,
  autoTrackLoginAction,
} from '../actions';

function* sessionTokenRequest() {
  try {
    yield put(
      authActions.update({
        isGettingSessionToken: true,
      }),
    );

    const { data } = yield sessionRegisterToken();
    yield put(
      authActions.update({
        sessionToken: data.data.token,
        isGettingSessionToken: false,
      }),
    );
  } catch (e: any) {
    console.log('sessionTokenRequest', e);
    yield put(
      authActions.update({
        isGettingSessionToken: false,
      }),
    );
  }
}

function* loginRequest(action: AsyncAction<LoginMeta, LoginPayload>) {
  try {
    const { fromSignup } = action.meta;
    const { data: result } = yield sessionRegisterToken();
    const { data } = yield call(login, {
      ...action.meta,
      token: result.data.token,
    });

    yield put(loginActions.success({ ...data, fromSignup }));

    yield put(autoTrackLoginAction.request({ action: TrackAutoType.auto }));
  } catch (e: any) {
    let message;
    if (e && Object.keys(e).length === 0) {
      message = 'Server busy, please try again later';
    } else {
      message = e.response?.data?.message || e.message;
    }
    yield call(sessionTokenRequest);
    yield put(
      loginActions.failed(
        JSON.stringify({
          message,
          headline: e.response?.data?.messageHeader,
        }),
      ),
    );
  }
}

function* handleCanvaLoginFlow(canvaUrl: string) {
  sessionStorage.removeItem('login_redirect');
  sessionStorage.removeItem('canva_url');
  yield (window.location.href = canvaUrl);
}

function persistAmplifiedTemplateShare(urlParams: URLSearchParams | null) {
  const shareAdvocacyPostId = urlParams?.get('shareAdvocacyPostId');
  if (!shareAdvocacyPostId) return;

  sessionStorage.setItem(
    SessionOpenItem.AMPLIFIED_TEMPLATE_SHARE,
    shareAdvocacyPostId,
  );
}

/**
 * Share invite emails deep-link to one specific share. When the advocate is logged
 * out, the advocate route guard forwards that id here as `?shareId=`; stash it so
 * My Shares can open that share once its list loads (TribesV2 reads STORY_SHARE).
 * Without this the advocate lands on the plain My Shares list. IPS-820.
 */
function persistStoryShareOpenItem(urlParams: URLSearchParams | null) {
  const shareId = urlParams?.get('shareId');
  if (!shareId) return;

  sessionStorage.setItem(SessionOpenItem.STORY_SHARE, shareId);
}

/**
 * SSO advocates reach us through the IdP callback rather than a login form, so their
 * share id arrives on the login payload instead of as `?shareId=` on the URL. Stash it
 * here, before the first-login gate below: that gate sends brand new advocates to
 * profile completion and returns early, and the onboarding screen after it reads
 * STORY_SHARE to route them on to My Shares. IPS-855.
 */
function persistStoryShareOpenItemFromPayload(
  payloadData: LoginPayload['data'],
) {
  const shareStoryId = payloadData?.shareStoryId;
  if (!shareStoryId) return;

  sessionStorage.setItem(SessionOpenItem.STORY_SHARE, shareStoryId);
}

function resolveRedirectURL(urlParams: URLSearchParams | null) {
  return urlParams?.get('redirect');
}

function parseBooleanLike(value: unknown): boolean | null {
  if (value === true || value === 'true' || value === '1') return true;
  if (value === false || value === 'false' || value === '0') return false;
  return null;
}

function* handleStoryShareOpenItemRedirect(shareStoryId: any) {
  const openItem = shareStoryId || false;
  if (!openItem) return false;

  sessionStorage.setItem(SessionOpenItem.STORY_SHARE, openItem);
  yield call(navigationService.goTo, ADVOCATE_ROUTES.TRIBES);
  return true;
}

function* handleSsoFirstLoginAdvocateRedirect(
  payloadData: LoginPayload['data'],
) {
  const isFirst = payloadData?.isFirst;
  const shouldGoToProfile =
    isFirst === true || isFirst === 'true' || isFirst === '1';

  const fromSSOLoginCallback = payloadData?.fromSSOLoginCallback === true;
  const companyId = payloadData?.companyId;

  if (
    !fromSSOLoginCallback ||
    payloadData?.platform !== PlatForm.ADVOCATE ||
    !shouldGoToProfile ||
    !companyId
  ) {
    return false;
  }

  yield call(
    navigationService.goTo,
    ADVOCATE_ROUTES.REGISTER_PROFILE(companyId),
    {
      state: { signUpAnotherPlatform: true, fromSSOFirstLogin: true },
    },
  );
  return true;
}

function shouldShowForceFinishRegistrationPopup(
  payloadData: LoginPayload['data'],
  redirectURL: string | null | undefined,
) {
  const fromSSOLoginCallback = payloadData?.fromSSOLoginCallback === true;
  const companyId = payloadData?.companyId;
  const isAdvocate = payloadData?.platform === PlatForm.ADVOCATE;
  const isCompleteProfile = parseBooleanLike(payloadData?.isCompleteProfile);
  const forceFinishRegistration = parseBooleanLike(
    payloadData?.forceFinishRegistration,
  );

  return (
    fromSSOLoginCallback &&
    !!companyId &&
    isAdvocate &&
    !redirectURL &&
    isCompleteProfile === false &&
    forceFinishRegistration === true
  );
}

/**
 * NOTE: This saga currently handles multiple responsibilities in one place.
 * Keep the order of these blocks when updating logic to avoid redirect regressions:
 * 1) Read canva_url from sessionStorage (redirect deferred to step 9)
 * 2) Parse query params + persist session open items
 * 3) Resolve redirect target + locale
 * 4) Handle existing-EB registration shortcut flow
 * 5) Persist auth state
 * 6) Handle SSO first-login advocate redirect
 * 7) Handle share-story open item redirect
 * 8) Handle force-finish-registration redirect
 * 9) Execute default post-login navigation by platform
 */
export function* loginSuccess(
  action: AsyncAction<LoginMeta, LoginPayload>,
): any {
  const locationSearch: string | null =
    navigationService.getCurrentLocation()?.search || null;

  const urlParams: URLSearchParams | null = locationSearch
    ? new URLSearchParams(locationSearch)
    : null;

  // Step 1: read canva URL early but redirect only after auth state is stored (Step 9).
  const canvaUrl = sessionStorage.getItem('canva_url');

  // Step 2: parse URL params and persist share/open session values.
  persistAmplifiedTemplateShare(urlParams);
  persistStoryShareOpenItem(urlParams);
  persistStoryShareOpenItemFromPayload(action.payload.data);

  // I still wonder is that used?
  const redirectURL = resolveRedirectURL(urlParams);

  const registerEBWithExistedEmail = urlParams?.get(
    'registerEBWithExistedEmail',
  );

  const ebFillData = urlParams?.get('ebFillData');

  // Step 3: apply locale and resolve special shortcut flows.
  const languageCode = action.payload.data.language?.code;
  if (languageCode) i18n(languageCode);

  if (registerEBWithExistedEmail && ebFillData) {
    // Step 4: persist authenticated user/session context to redux.
    yield put(
      registerEbWithExistedAccountActions.request(JSON.parse(ebFillData)),
    );
  } else {
    yield put(
      authActions.update({
        token: action.payload.data.token,
        companyId: action.payload.data.companyId,
        platform: action.payload.data.platform,
        refresh_token: action.payload.data.refresh_token,
        logoutUrl: action.payload.data?.logoutSSOUrl,
        isFirst: action.payload.data?.isFirst,
        isCompleteProfile: parseBooleanLike(
          action.payload.data?.isCompleteProfile,
        ),
        forceFinishRegistration:
          parseBooleanLike(action.payload.data?.forceFinishRegistration) ===
          true,
        system_message: action.payload?.data?.system_message,
        permissionRouter: action.payload?.data?.access_routes || [],
      }),
    );

    // Step 6: SSO-specific advocate first-login redirect gate.
    const handledSsoFirstLoginRedirect =
      yield* handleSsoFirstLoginAdvocateRedirect(action.payload.data);

    if (handledSsoFirstLoginRedirect) {
      return;
    }

    // Step 7: share-story open item redirect.
    const handledStoryShareOpenItemRedirect =
      yield* handleStoryShareOpenItemRedirect(action.payload.data.shareStoryId);

    if (handledStoryShareOpenItemRedirect) {
      return;
    }

    // Step 8: force-finish-registration redirect.
    const shouldShowForceFinishRegistrationModal =
      shouldShowForceFinishRegistrationPopup(action.payload.data, redirectURL);

    yield put(
      authActions.update({
        showForceFinishRegistrationModal:
          shouldShowForceFinishRegistrationModal === true,
      }),
    );

    // Step 9: default post-login navigation tree.
    yield put(getOnboardActions.request());

    if (canvaUrl) {
      yield* handleCanvaLoginFlow(canvaUrl);
    } else if (redirectURL) {
      sessionStorage.removeItem('login_redirect');
      yield call(navigationService.goTo, redirectURL);
    }

    if (action.payload.data?.storyId) {
      yield call(
        navigationService.goTo,
        `${ADVOCATE_ROUTES.ROOT}/story/${action.payload.data.storyId}`,
      );
    } else if (action.payload.data.platform === PlatForm.ADVOCATE) {
      yield call(
        navigationService.goTo,
        redirectURL ? redirectURL : ADVOCATE_ROUTES.STORY_MANAGEMENT,
      );
    } else if (
      action.payload.data.platform === PlatForm.EMPLOYER ||
      action.payload.data.platform === PlatForm.RECRUITER
    ) {
      if (action.payload.fromSignup) {
        yield call(
          navigationService.goTo,
          redirectURL ? redirectURL : EMPLOYER_ROUTES.ONBOARDING,
        );
      } else {
        let URL = EMPLOYER_ROUTES.CONTENT_CALENDAR;
        if (action.payload.data.token) {
          const { data } = yield call(
            getAllPlatform,
            action.payload.data.token,
          );
          if (action.payload.data?.platform === PlatForm.RECRUITER) {
            URL = data?.data?.recruiterJD
              ? EMPLOYER_ROUTES.HOMEPAGE_JD
              : EMPLOYER_ROUTES.HOMEPAGE;
          } else if (data?.data?.renderHomePage) URL = EMPLOYER_ROUTES.HOMEPAGE;
        }
        yield call(navigationService.goTo, URL);
      }
    }
  }
}

function* loginFailed(action: AsyncAction<LoginMeta, LoginPayload>) {
  const actionError = JSON.parse(action.error);
  let error = '';

  if (actionError?.message?.toLocaleLowerCase().includes('timeout')) {
    error = 'Uh-oh! Your login attempt has timed out.';
  } else {
    error = actionError.message;
  }

  yield put(openToast(error, 'error', actionError?.headline));
}

function* dismissMessage() {
  yield put(
    authActions.update({
      system_message: '',
    }),
  );
}

function* accessRoutesRequest(
  action: AsyncAction<AccessRouterMeta, GenericResponse>,
): any {
  const state: Store = yield select();
  if (state.auth.token)
    try {
      const { data } = yield accessRouter(
        action.meta.platform,
        state.auth.token,
      );
      yield put(
        authActions.update({
          permissionRouter: data.data.access_routes || [],
        }),
      );
    } catch (e: unknown) {
      console.log('accessRoutesRequest', e);
    }
}

function* loginWatcher(): any {
  yield takeLatest(loginActions.REQUEST, loginRequest);
  yield takeLatest(loginActions.SUCCESS, loginSuccess);
  yield takeLatest(loginActions.FAILED, loginFailed);
  yield takeLatest('DISMISS_SYSTEM_MESSAGE', dismissMessage);
  yield takeLatest(accessRouterActions.REQUEST, accessRoutesRequest);
  yield takeLatest(getSessionToken.REQUEST, sessionTokenRequest);
}

export default loginWatcher;
